import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import HudsonDarkCard from '../components/ui/hudson-dark-card'

const HudsonDarkCardDemo: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
      
      {/* Header */}
      <div className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-white mb-6">
              Hudson Club Elite Card
            </h1>
            <p className="text-2xl text-gray-300 leading-relaxed max-w-4xl mx-auto">
              Deep blue almost black with slow, mesmerizing animations. 
              A symbol of exclusivity for the rare few who experience effortless homeownership.
            </p>
          </div>
        </div>
      </div>

      {/* Main Card Showcase */}
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          
          {/* Primary Card Display */}
          <div className="text-center mb-20">
            <div className="flex justify-center mb-12">
              <HudsonDarkCard 
                memberName="ELITE MEMBER"
                membershipTier="Elite"
                memberSince="2024"
                className="transform hover:scale-105 transition-all duration-1000"
              />
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-6">
              For the Rare Few
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Each membership card is a masterpiece — featuring deep cosmic blues that shift and flow 
              like the night sky, with slow, hypnotic animations that reflect the tranquility of effortless homeownership.
            </p>
          </div>

          {/* Feature Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-800/30">
                <span className="text-blue-300 text-2xl">🌌</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Deep Cosmic Blues</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Almost black depths with subtle blue undertones that shift like the depths of space
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-800/30">
                <span className="text-blue-300 text-2xl">⏳</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Slow Animations</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Hypnotic 8-15 second cycles that mirror the peaceful rhythm of effortless living
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-800/30">
                <span className="text-blue-300 text-2xl">✨</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Elite Exclusivity</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                A tangible symbol of joining the rare few who experience true luxury
              </p>
            </div>
          </div>

          {/* Card Variations */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Membership Tiers
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Standard */}
              <div className="text-center">
                <div className="mb-6 flex justify-center">
                  <HudsonDarkCard 
                    memberName="STANDARD MEMBER"
                    membershipTier="Standard"
                    memberSince="2024"
                    className="scale-90"
                  />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Standard</h3>
                <p className="text-gray-400 text-sm">Essential elegance for smaller estates</p>
              </div>
              
              {/* Premium */}
              <div className="text-center">
                <div className="mb-6 flex justify-center">
                  <HudsonDarkCard 
                    memberName="PREMIUM MEMBER"
                    membershipTier="Premium"
                    memberSince="2023"
                    className="scale-95"
                  />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Premium</h3>
                <p className="text-gray-400 text-sm">Enhanced experience with priority service</p>
              </div>
              
              {/* Elite */}
              <div className="text-center">
                <div className="mb-6 flex justify-center">
                  <HudsonDarkCard 
                    memberName="ELITE MEMBER"
                    membershipTier="Elite"
                    memberSince="2022"
                    className="scale-100 ring-2 ring-blue-800/30"
                  />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Elite</h3>
                <p className="text-gray-400 text-sm">Ultimate luxury with personal home director</p>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div className="bg-black/40 rounded-2xl p-8 mb-16 border border-blue-900/20">
            <h2 className="text-2xl font-bold text-white text-center mb-8">
              Animation Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-blue-300 mb-4">Visual Features</h3>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li>• Deep blue (#0a0e1a) to black gradient</li>
                  <li>• Subtle cosmic particle system</li>
                  <li>• Slow pulsing glow effects (4s cycles)</li>
                  <li>• 3D hover transformations</li>
                  <li>• Backdrop blur and transparency layers</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-blue-300 mb-4">Animation Timing</h3>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li>• Base rotation: 8 second cycles</li>
                  <li>• Particle movement: 12 second flows</li>
                  <li>• Aurora effects: 15 second waves</li>
                  <li>• Hover transitions: 0.8s smooth curves</li>
                  <li>• Card interactions: 1s response time</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Integration Guide */}
          <div className="bg-gradient-to-r from-blue-900/20 to-black/40 rounded-2xl p-8 border border-blue-800/30">
            <h2 className="text-2xl font-bold text-white text-center mb-6">
              Perfect for Hudson Club
            </h2>
            <p className="text-lg text-gray-300 text-center mb-8">
              This deep, cosmic card design embodies the mystery and exclusivity of The Hudson Club. 
              The slow animations create a sense of calm luxury — perfect for homeowners who value 
              tranquility and effortless elegance.
            </p>
            
            <div className="text-center">
              <button className="bg-blue-800 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors">
                Request Your Invitation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HudsonDarkCardDemo 